import {Brick, ErrorFlow, QueryResult, registerBrick} from 'olympe';
import {formatWaysOfBuyingByLabel, getWayOfBuyingLabelValue} from '../wayOfBuying';
import {OneTimeVendor} from '../../models/OneTimeVendor';
import {checkLabelValueObject, createLabelValue} from '../checkObjects';
import {Account} from '../../models/financial_infos/Account';
import {Material} from '../../models/Material';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {
    EMPTY_DELIVERY_ADDRESS,
    EMPTY_LABEL_VALUE,
    getStringValueForSourceFromApiKey,
    SOURCES,
} from '../constants';
import {NBSValidation} from '../../models/NBSValidation';
import {SBValidation} from '../../models/SBValidation';
import {HeaderDetails} from '../../models/HeaderDetails';
import {AttachmentsInfo} from '../../models/AttachmentsInfo';
import {Attachment} from '../../models/attachments_info/Attachment';
import {POSendingOptions} from '../../models/POSendingOptions';
import {FinancialInfo} from '../../models/FinancialInfo';
import {
    checkCompanyCodePromise,
    getERSAndGRWithPromiseLambda,
    getMasterDataMultipleObjectsLambda, getMaterialByCodePromiseLambda,
    getPartnerFunctionWithPromiseLambda,
    getPSLInformationLambda, readUsersLambda,
} from '../functionSignatures';
import {isOTVorCMV, oTVorCMVorGeneric} from '../helpers';
import {AccountDetailsByMaterial} from '../../models/financial_infos/AccountDetailsByMaterial';
import {haveCommonElements} from '../array';
import {getUniquePrId} from '../../components/_helpers';
import {WaysOfBuying} from '../../models/WaysOfBuying';
import {
    determineWayOfBuying,
    findUser,
    getPropertyValue,
    labelValueValidator,
    vendorCodeValidator,
} from '../uploadExcel';
import {addNBusinessDays} from '../date';
import {fetchAndProcessIncoterms} from '../incotermsHelpers';
import PurchaseRequestDM from '../../models/cloudobjects/PurchaseRequestDM';
import {convertSourceToLabelValue} from '../sourceMappings';
/**
 * @typedef PrToImport
 * @type {Object}
 * @property {string} id
 * @property {PurchaseRequest} pr
 */

/**
 * A brick that create a PR JSON data from JSON data coming from the Excel file
 */
export default class ConvertExcelPayloadToPRJson extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {ListDef} headerSheet
     * @param {ListDef} itemSheet
     * @param {*} readUsers
     * @param {boolean} isDYNEnabled
     * @param {*} getMasterDataMultiple
     * @param {*} getPSLInformation
     * @param {*} userDefaults
     * @param {*} checkCompanyCode
     * @param {*} allPRs
     * @param {*} getERSAndGR
     * @param {*} getPartnerFunction
     * @param {*} getIncoterms
     * @param {*} getAllIncoterms
     * @param {*} getMaterialByCode
     * @param {function()} forwardEvent
     * @param {function(*)} setResult
     * @param {function(*)} setInvalidPrs
     * @param {function(*)} setNotCreatedPrs
     * @param {function(*)} setValidPrStatus
     * @param {function(*)} setErrorFlow
     */
    async update($, [headerSheet, itemSheet, readUsers, isDYNEnabled, getMasterDataMultiple, getPSLInformation, userDefaults, checkCompanyCode, allPRs, getERSAndGR, getPartnerFunction, getIncoterms, getAllIncoterms, getMaterialByCode], [forwardEvent, setResult, setInvalidPrs, setNotCreatedPrs, setValidPrStatus, setErrorFlow]) {
        try {
            const prsToImport = await this.readExcelFile($, headerSheet, itemSheet, getPSLInformation, readUsers, isDYNEnabled, userDefaults, checkCompanyCode, allPRs, getERSAndGR, getPartnerFunction, getIncoterms, getAllIncoterms, getMaterialByCode);
            const validPrItems = prsToImport.filter((item)=> item.invalidPslInformation?.length === 0 && item.errorPrs?.length === 0);
            const invalidPrItems = prsToImport.filter((item)=>item.invalidPslInformation?.length > 0);
            const notCreatedPrItems = prsToImport.filter((item)=>item.errorPrs?.length > 0);
            const validPrStatus = [];

            if (validPrItems.length > 0) {
                validPrItems.forEach((item)=> {
                    delete item.errorPrs;
                    delete item.invalidPslInformation;
                    if (item.warningPRs.length > 0) {
                        validPrStatus.push({eprId: item.pr.EPRId, type: 'WARNING', msg: item.warningPRs.map((warning) => warning.msg)?.join(', ')});
                    } else {
                        validPrStatus.push({eprId: item.pr.EPRId, type: 'SUCCESS', msg: ''});
                    }
                });
            }

            try {
                this.fetchMasterData($, getMasterDataMultiple, validPrItems)
                    .then((results) => {
                        // We need the plants' result to fetch the delivery address
                        this.fetchDeliveryAddress($, results, getMasterDataMultiple)
                            .then((addresses) => {
                                const md = this.readResults([...results, addresses]);
                                validPrItems?.forEach((pr) => {
                                    this.updatePR(pr.pr, md);
                                });
                                setResult(validPrItems);
                                setInvalidPrs(invalidPrItems);
                                setNotCreatedPrs(notCreatedPrItems);
                                setValidPrStatus(validPrStatus);
                                forwardEvent();
                            }).catch((e)=>{
                                console.error(e);
                                setErrorFlow(ErrorFlow.create(e.message, 1));
                            });
                    }).catch((e)=>{
                        console.error(e);
                        setErrorFlow(ErrorFlow.create(e.message, 1));
                    });
            } catch (e) {
                console.error('Error fetching master data:', e);
                // Proceed without master data updates
                setResult(validPrItems);
                setInvalidPrs(invalidPrItems);
                setNotCreatedPrs(notCreatedPrItems);
                setValidPrStatus(validPrStatus);
                forwardEvent();
            }
        } catch (e) {
            console.error(e);
            setErrorFlow(ErrorFlow.create(e.message, 1));
        }
    }

    /**
     * Groups purchase requests by the line item number.
     * @param {BrickContext} $
     * @param {[]} headerSheet
     * @param {[]} itemSheet
     * @param {*} getPSLInformation
     * @param {*} readUsers
     * @param {boolean} isDYNEnabled
     * @param {{}} userDefaults
     * @param {*} checkCompanyCode
     * @param {*} allPRs
     * @param {*} getERSAndGR
     * @param {*} getPartnerFunction
     * @param {*} getIncoterms
     * @param {*} getAllIncoterms
     * @param {*} getMaterialByCode
     * @return {PrToImport[]}
     */
    async readExcelFile($, headerSheet, itemSheet, getPSLInformation, readUsers, isDYNEnabled, userDefaults, checkCompanyCode, allPRs, getERSAndGR, getPartnerFunction, getIncoterms, getAllIncoterms, getMaterialByCode) {
        /**
         * @type {PrToImport[]}
         */
        const prsToImport = [];
        // TODO Not working as we don't have QueryResult (Purchase request property). And it needs to be updated because we can't get all the PRs without any filter.
        const allOpenPrs = (allPRs instanceof QueryResult ? allPRs.toArray() : [])
            ?.map((r) => {
                const jsonFromPR = r.get(PurchaseRequestDM.jsonProp);
                if (jsonFromPR) {
                    return ({pr: new PurchaseRequest(jsonFromPR), tag: r.getTag()});
                } else {
                    return ({pr: {}, tag: r.getTag()});
                }
            });

        const userDefaultsData = {};

        let companyCode;
        const companyCodeString = userDefaults.companyCode || userDefaults['company_code'] || '';
        const companyCodeParts = companyCodeString.split(' - ');
        const companyCodeUD = companyCodeParts[0]?.trim() || '';
        userDefaultsData.companyCode = createLabelValue(companyCodeString, companyCodeUD);

        const plantString = userDefaults.plant || userDefaults['plant'] || '';
        const plantParts = plantString.split(' - ');
        const plantUD = plantParts[0]?.trim() || '';
        userDefaultsData.plant = createLabelValue(plantString, plantUD);

        const purchaseOrganizationString = userDefaults.purchaseOrganization || userDefaults['purchase_organization'] || '';
        const purchaseOrganizationParts = purchaseOrganizationString.split(' - ');
        const purchaseOrganizationUD = purchaseOrganizationParts[0]?.trim() || '';
        userDefaultsData.purchaseOrganization = createLabelValue(purchaseOrganizationString, purchaseOrganizationUD);

        const purchasingGroupString = userDefaults.purchasingGroup || userDefaults['purchasing_group'] || '';
        const purchasingGroupParts = purchasingGroupString.split(' - ');
        const purchasingGroupUD = purchasingGroupParts[0]?.trim() || '';
        userDefaultsData.purchasingGroup = createLabelValue(purchasingGroupString, purchasingGroupUD);

        userDefaultsData.hidePrice = userDefaults.hidePrice ||userDefaults['hide_price'] || false;

        const currencyString = userDefaults.currency || userDefaults['currency'] || '';
        const currencyParts = currencyString.split(' - ');
        const currencyValue = currencyParts[0]?.trim() || '';
        const currencyLabel = currencyString || '';
        userDefaultsData.currency = createLabelValue(currencyLabel, currencyValue);

        userDefaultsData.spender = userDefaults.spender ? JSON.parse(userDefaults.spender) : null;
        userDefaultsData.requester = userDefaults.requester ? JSON.parse(userDefaults.requester) : null;

        const taxCodeString = userDefaults.taxCode || userDefaults['tax_code'] || '';
        const taxCodeParts = taxCodeString.split(' - ');
        const taxCodeValue = taxCodeParts[0]?.trim() || '';
        const taxCodeLabel = taxCodeString || '';
        userDefaultsData.taxCode = createLabelValue(taxCodeLabel, taxCodeValue);

        const storageLocationString = userDefaults.storageLocation || userDefaults['storage_location'] || '';
        const storageLocationParts = storageLocationString.split(' - ');
        const storageLocationUD = storageLocationParts[0]?.trim() || '';
        userDefaultsData.storageLocation = createLabelValue(storageLocationString, storageLocationUD);

        const deliveryAddressString = userDefaults.deliveryAddress || userDefaults['delivery_address'];
        let deliveryAddressUD = EMPTY_DELIVERY_ADDRESS;
        if (deliveryAddressString) {
            try {
                const parsedAddress = JSON.parse(deliveryAddressString);
                deliveryAddressUD = {
                    name: parsedAddress.name || '',
                    name2: parsedAddress.name2 || '',
                    houseNumber: parsedAddress.houseNumber || '',
                    street: parsedAddress.street || '',
                    postalCode: parsedAddress.postalCode || '',
                    city: parsedAddress.city || '',
                    state: parsedAddress.state || '',
                    country: parsedAddress.country || '',
                    manuallyModified: parsedAddress.manuallyModified || false,
                };
            } catch (e) {
                console.warn('Failed to parse delivery address from userDefaults:', e);
            }
        }
        userDefaultsData.deliveryAddress = deliveryAddressUD;

        const wbsString = userDefaults.wbs_project || userDefaults['wbs_project'] || '';
        const wbsParts = wbsString.split(' - ');
        const wbsValue = wbsParts[0]?.trim() || '';
        userDefaultsData.wbs = createLabelValue(wbsString, wbsValue);

        const costCenterString = userDefaults.cost_center || userDefaults['cost_center'] || '';
        const costCenterParts = costCenterString.split(' - ');
        const costCenterValue = costCenterParts[0]?.trim() || '';
        userDefaultsData.costCenter = createLabelValue(costCenterString, costCenterValue);

        const profitCenterString = userDefaults.profit_center || userDefaults['profit_center'] || '';
        const profitCenterParts = profitCenterString.split(' - ');
        const profitCenterValue = profitCenterParts[0]?.trim() || '';
        userDefaultsData.profitCenter = createLabelValue(profitCenterString, profitCenterValue);

        const glNumberString = userDefaults.gl_number || userDefaults['gl_number'] || '';
        const glNumberParts = glNumberString.split(' - ');
        const glNumberValue = glNumberParts[0]?.trim() || '';
        userDefaultsData.glNumber = createLabelValue(glNumberString, glNumberValue);

        // Read the header sheet
        for (const header1 of headerSheet
            .filter((header) => this.readField(header, 'EPR Id', false, false) !== '')) {
            const dataSources = {
                excel: {},
                psl: {},
                userDefaults: {...userDefaultsData},
                masterData: {},
                default: {
                    sendPOToRequester: true,
                    sendPOToSpender: true,
                    sendPOToOther: false,
                    othersAddress: '',
                    sendPOToSupplier: false,
                    supplierAddress: '',
                    requestedDeliveryDate: addNBusinessDays(new Date(), 5).toUTCString(),
                },
            };

            const id = this.readField(header1, 'EPR Id', false, false);
            const isUpdatePr = id.includes('EPR-');
            let prExists = true;
            let foundPR = null;
            if (isUpdatePr) {
                // first let's check if the pr is in the list
                foundPR = allOpenPrs.filter((item)=>'EPR-'+item.pr?.EPRId === id)?.[0];
                prExists = !!foundPR;
                if (!prExists) {
                    prsToImport.push({
                        id: id,
                        invalidPslInformation: [],
                        errorPrs: [{id: id, msg: 'The current PR is either nonexistent or has already been submitted.'}],
                        warningPRs: [],

                    });
                }
            }
            if (prExists) {
                const projectTitle = this.readField(header1, 'Project title', false, false);
                const vendorCode = this.readField(header1, 'Vendor code', false, false, '99');

                let otvCmvName;
                let otvCmvName2;
                let otvHouseNumber;
                let otvCmvStreet;
                let otvCmvPostalCode;
                let otvCmvCity;
                let otvCmvState;
                let otvCmvCountry;
                let otvCmvPhoneNumber;
                // let otvCmvBankingDetails;
                // let otvCmvSponsorshipRequestLetter;
                // let otvCmvSpeakerAcceptanceLetter;

                let otv;

                if (isOTVorCMV(vendorCode)) {
                    otvCmvName = this.readField(header1, 'OTV/CMV name', false, false, 'Undefined');
                    otvCmvName2 = this.readField(header1, 'OTV/CMV name 2', false, false, 'Undefined');
                    otvHouseNumber = this.readField(header1, 'House number', false, false);
                    otvCmvStreet = this.readField(header1, 'Street', false, false);
                    otvCmvPostalCode = this.readField(header1, 'Postal code', false, false);
                    otvCmvCity = this.readField(header1, 'City', false, false);
                    otvCmvState = this.readField(header1, 'State/Province/Area', false, false);
                    otvCmvCountry = this.readField(header1, 'Country', false, false);
                    otvCmvPhoneNumber = this.readField(header1, 'Phone number', false, false);
                    // otvCmvBankingDetails = this.readField(header, 'For OTV or CMV: Attachment (banking details)', false, false);
                    // otvCmvSponsorshipRequestLetter = this.readField(header, 'For CMV only: Attachment (sponsorship request letter)', false, false);
                    // otvCmvSpeakerAcceptanceLetter = this.readField(header, 'For CMV only: Attachment (speaker acceptance letter)', false, false);
                    otv = new OneTimeVendor(
                        otvCmvName,
                        {
                            name: '',
                            name2: otvCmvName2,
                            houseNumber: otvHouseNumber,
                            street: otvCmvStreet,
                            postalCode: otvCmvPostalCode,
                            city: otvCmvCity,
                            state: otvCmvState,
                            country: otvCmvCountry,
                            manuallyModified: false,
                        },
                        otvCmvPhoneNumber,
                        oTVorCMVorGeneric(vendorCode),
                    );
                } else {
                    otv = new OneTimeVendor(
                        '',
                        EMPTY_DELIVERY_ADDRESS,
                        '',
                        OneTimeVendor.supplierTypes.generic,
                    );
                }

                const wayOfBuyingLabel = this.readField(header1, 'Way of buying', false, false);
                const spender = this.readField(header1, 'Spender', false, false);
                const requester = this.readField(header1, 'Requester', false, false);
                companyCode = this.readField(header1, 'Company code', true, false, '', true);
                const purchaseOrg = this.readField(header1, 'Purchase organization', true, false, '', true);
                const purchaseOrgValue = this.readField(header1, 'Purchase organization', false, false, '', true);
                const purchasingGroup = this.readField(header1, 'Purchasing group', true, false, '', true);
                const validityStart = this.readField(header1, 'Validity start (FO)', false, false);
                const validityEnd = this.readField(header1, 'Validity end (FO)', false, false);
                const alternativePTs = this.readField(header1, 'Alternative payment terms', true, false);
                const incoterms = this.readField(header1, 'Incoterms', true, false);
                const incoterms2 = this.readField(header1, 'Incoterms 2', false, false);
                const partnerOrderingAddress = this.readField( header1, 'Ordering address', true, false);
                const invoicePartner = this.readField( header1, ' Invoice Partner', true, false);
                let goodReceipt = this.readField(header1, 'Good Receipt Required', false, true);
                let grBasedInvoice = this.readField(header1, 'GR-Based Invoice Verification', false, true);
                let ers = this.readField(header1, 'ERS', false, true);
                const internalComment = this.readField(header1, 'Internal comment (headers)', false, false);
                const externalComment = this.readField(header1, 'External comment (headers)', false, false);
                const sendPOToRequester = this.readField(header1, 'Send PO PDF to requester', false, true);
                const sendPOToSpender = this.readField(header1, 'Send PO PDF to spender', false, true);
                const sendPOToOther = this.readField(header1, 'Send PO PDF to other', false, true);
                const sendPOToSupplier = this.readField(header1, 'Send PO PDF to supplier', false, true);
                const othersAddress = this.readField(header1, 'Other\'s address', false, false);
                const supplierAddress = this.readField(header1, 'Supplier address', false, false);
                const hidePrice = this.readField(header1, 'Hide Price', false, true);

                const incotermsLabelValue = createLabelValue(
                    incoterms2 || incoterms.label || '',
                    incoterms.value || '',
                );

                const incotermsObject = {
                    data: incotermsLabelValue,
                    manuallyModified: incoterms2 !== '',
                };

                dataSources.excel.wayOfBuying = wayOfBuyingLabel ? getWayOfBuyingLabelValue(wayOfBuyingLabel) : null;

                const wayOfBuyingLabelValue = getPropertyValue('wayOfBuying', dataSources, {}, 'header');
                const wob = formatWaysOfBuyingByLabel(wayOfBuyingLabelValue ? wayOfBuyingLabelValue.label : null);

                if (wob.currentValue.value === 'FO') {
                    goodReceipt = false;
                    grBasedInvoice = false;
                    ers = false;
                } else {
                    if (!header1.hasOwnProperty('Good Receipt Required')) {
                        goodReceipt = true;
                    }
                }

                const getSpenderInfoPromise = readUsersLambda($, readUsers, spender);
                const getRequesterInfoPromise = readUsersLambda($, readUsers, requester);

                const results = await Promise.all([getSpenderInfoPromise, getRequesterInfoPromise]);

                const spenderInfo = findUser(spender, JSON.parse(results[0][0]));
                const requesterInfo = findUser(requester, JSON.parse(results[1][0]));

                // Populate dataSources with Excel info
                dataSources.excel.vendorCode = vendorCode;
                dataSources.excel.vendorName = 'Undefined';
                dataSources.excel.spender = spenderInfo;
                dataSources.excel.requester = requesterInfo;
                dataSources.excel.companyCode = companyCode;
                dataSources.excel.purchaseOrganization = purchaseOrg;
                dataSources.excel.purchasingGroup = purchasingGroup;
                dataSources.excel.sendPOToRequester = sendPOToRequester;
                dataSources.excel.sendPOToSpender = sendPOToSpender;
                dataSources.excel.sendPOToOther = sendPOToOther;
                dataSources.excel.sendPOToSupplier = sendPOToSupplier;
                dataSources.excel.othersAddress = othersAddress;
                dataSources.excel.supplierAddress = supplierAddress;
                dataSources.excel.hidePrice = hidePrice;
                dataSources.excel.grBasedInvoice = grBasedInvoice;
                dataSources.excel.ers = ers;
                dataSources.excel.partnerOrderingAddress = partnerOrderingAddress;
                dataSources.excel.invoicePartner = invoicePartner;
                dataSources.excel.incoterms = incotermsObject;

                if (grBasedInvoice === undefined || ers === undefined) {
                    try {
                        const ersAndGrResult = await getERSAndGRWithPromiseLambda($, getERSAndGR, vendorCode, purchaseOrgValue);

                        if (ersAndGrResult && ersAndGrResult.length > 0) {
                            const ERSAndGRData = JSON.parse(ersAndGrResult[0]);

                            if (ERSAndGRData.length > 0) {
                                dataSources.masterData.grBasedInvoice = ERSAndGRData[0]?.webre === 'X';
                                dataSources.masterData.ers = ERSAndGRData[0]?.xersy === 'X';
                            }
                        } else {
                            console.error('Error fetching ERS and GR');
                        }
                    } catch (error) {
                        console.error('Error fetching ERS and GR:', error);
                    }
                }

                const partnerFunctions = ['BA', 'RS'];
                for (const partnerFunction of partnerFunctions) {
                    let amdrPartnerFunction = '';
                    if (partnerFunction === 'OA') {
                        amdrPartnerFunction = 'BA';
                    } else if (partnerFunction === 'PI') {
                        amdrPartnerFunction = 'RS';
                    }
                    try {
                        const plant = dataSources.userDefaults.plant?.value || '';
                        const partnerFunctionResult = await getPartnerFunctionWithPromiseLambda($, getPartnerFunction, vendorCode, purchaseOrgValue, amdrPartnerFunction, plant);
                        if (partnerFunctionResult && partnerFunctionResult.length > 0) {
                            const partnerFunctionData = JSON.parse(partnerFunctionResult[0]);
                            if (partnerFunctionData.length > 0) {
                                const uniquePartners = partnerFunctionData.filter((value, index, self) =>
                                    index === self.findIndex((t) => (
                                        t['lifn2'] === value['lifn2'] && t['name1'] === value['name1'] && t['ort01'] === value['ort01']
                                    )),
                                );
                                if (uniquePartners.length > 1) {
                                    const joinedValues = uniquePartners.map((pf) => pf.lifn2).join(';');
                                    dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {
                                        label: '',
                                        value: joinedValues,
                                    };
                                } else if (uniquePartners.length === 1) {
                                    dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {
                                        label: `${uniquePartners[0].lifn2} - ${uniquePartners[0].name1} - ${uniquePartners[0].ort01}`,
                                        value: '',
                                    };
                                } else {
                                    dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {label: '', value: ''};
                                }
                            } else {
                                dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {label: '', value: ''};
                            }
                        } else {
                            dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {label: '', value: ''};
                        }
                    } catch (error) {
                        console.error(`Error fetching ${partnerFunction} data:`, error);
                        dataSources.masterData[partnerFunction === 'OA' ? 'orderingAddress' : 'invoicePartner'] = {label: '', value: ''};
                    }
                }

                if (!incoterms.value) {
                    try {
                        const {code, description} = await fetchAndProcessIncoterms({
                            context: $,
                            getIncoterms,
                            getAllIncoterms,
                            vendorCode: vendorCode,
                            purchaseOrgValue: purchaseOrgValue,
                        });

                        const incotermsFromMasterData = createLabelValue(description || '', code || '');

                        dataSources.masterData.incoterms = {
                            data: incotermsFromMasterData,
                            manuallyModified: false,
                        };
                    } catch (error) {
                        console.error('Error fetching incoterms:', error);
                        dataSources.masterData.incoterms = {
                            data: createLabelValue('', ''),
                            manuallyModified: false,
                        };
                    }
                }

                // We create a PR without material and financial info, as it will come later
                prsToImport.push({
                    id: id,
                    pr: new PurchaseRequest(
                        'Undefined',
                        vendorCode,
                        otv,
                        new NBSValidation([], '', [], ''),
                        new SBValidation([], '', ''),
                        new Date().toDateString(),
                        '',
                        projectTitle,
                        new WaysOfBuying(wob.currentValue, wob.availableOptions),
                        spenderInfo,
                        requesterInfo,
                        '',
                        `Please send the PDF of the PO to:\n---\nRequester: \nSpender: \nSupplier: \nOther: \n---\nProject/Order title: ${projectTitle || ''}\n\n${internalComment ?? ''}`,
                        externalComment,
                        new HeaderDetails(
                            [],
                            companyCode,
                            purchaseOrg,
                            purchasingGroup,
                            EMPTY_LABEL_VALUE,
                            [],
                            EMPTY_LABEL_VALUE,
                            EMPTY_LABEL_VALUE,
                            EMPTY_DELIVERY_ADDRESS,
                            '',
                            validityStart,
                            validityEnd,
                            EMPTY_LABEL_VALUE,
                            alternativePTs,
                            partnerOrderingAddress,
                            invoicePartner,
                            incotermsObject,
                            'Blank',
                            ers,
                            grBasedInvoice,
                            goodReceipt,
                        ),
                        new POSendingOptions(
                            sendPOToRequester,
                            sendPOToSpender,
                            sendPOToOther,
                            othersAddress,
                            sendPOToSupplier,
                            [supplierAddress],
                            false,
                            false,
                            false,
                            hidePrice,
                            '',
                            false,
                        ),
                        new FinancialInfo(
                            false,
                            [],
                            [],
                        ),
                        [],
                        isUpdatePr ?
                            new AttachmentsInfo(
                                foundPR.pr.attachmentsInfo?.mainAttachments.map((item)=> new Attachment(item.file, item.topic, item.user, item.date, item.name, item.userId)) || [],
                                foundPR.pr.attachmentsInfo?.competitiveBiddingJustificationAttachments.map((item)=> new Attachment(item.file, item.topic, item.user, item.date, item.name, item.userId)) || [],
                                '',
                                foundPR.pr.attachmentsInfo?.cbFormId || '',
                                foundPR.pr.attachmentsInfo?.exception || false,
                                foundPR.pr.attachmentsInfo?.selectedException || '',
                                foundPR.pr.attachmentsInfo?.complianceIndicator || '') :
                            new AttachmentsInfo([], [], '', '', false, '', ''),
                        PurchaseRequest.statuses.created,
                        [],
                        10,
                        true,
                        SOURCES.DYN,
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        null,
                        getStringValueForSourceFromApiKey(SOURCES.DYN, ''),
                        convertSourceToLabelValue('NCT'),
                    ),
                    dataSources: dataSources,
                    invalidPslInformation: [],
                    errorPrs: [],
                    warningPRs: [],
                    tag: foundPR?.tag || '',
                });
            }
        }

        // Read only the materials with a material number (and an EPR id)
        const DYNItems = itemSheet
            .filter((item) => (this.readField(item, 'EPR Id', false, false) !== '') &&
                (this.readField(item, 'Material number', false, false) !== ''));
        for (const item of DYNItems) {
            if (!isDYNEnabled) {
                throw ErrorFlow.create(`You can't import material without PSL material ID as DYN is not enabled for your company code.`, 1);
            }

            const id = this.readField(item, 'EPR Id', false, false);
            const prToUpdate = prsToImport.find((prToImport) => prToImport.id === id);

            const dataSources = {
                excel: {
                    currency: this.readField(item, 'Currency', true, false),
                    taxCode: this.readField(item, 'Tax code', true, false),
                    requestedDeliveryDate: this.readField(item, 'Requested delivery date', false, false),
                    plant: this.readField(item, 'Plant (line item)', true, false),
                    storageLocation: this.readField(item, 'Storage Location (line item)', true, false),
                },
                userDefaults: {
                    currency: prToUpdate.dataSources.userDefaults.currency,
                    taxCode: prToUpdate.dataSources.userDefaults.taxCode,
                    plant: prToUpdate.dataSources.userDefaults.plant,
                    storageLocation: prToUpdate.dataSources.userDefaults.storageLocation,
                },
                default: {
                    requestedDeliveryDate: prToUpdate.dataSources.default.requestedDeliveryDate,
                },
            };

            const materialNumber = this.readField(item, 'Material number', false, false);
            let description = this.readField(item, 'Description', false, false);
            const quantity = this.readField(item, 'Quantity', false, false);
            const uoM = this.readField(item, 'Unit of measure', false, false);
            const price = this.readField(item, 'Price', false, false);
            const per = this.readField(item, 'Price per', false, false);
            const currency = getPropertyValue('currency', dataSources, {}, 'items') || prToUpdate.dataSources.userDefaults.currency;
            const freeItem = this.readField(item, 'Free Item?', false, true);
            const taxCode = getPropertyValue('taxCode', dataSources, {}, 'items') || EMPTY_LABEL_VALUE;
            const requestedDeliveryDate = getPropertyValue('requestedDeliveryDate', dataSources, {}, 'items');
            const plant = getPropertyValue('plant', dataSources, {}, 'items') || EMPTY_LABEL_VALUE;
            const storageLoc = getPropertyValue('storageLocation', dataSources, {}, 'items') || EMPTY_LABEL_VALUE;
            const name = this.readField(item, 'Delivery address - Name', false, false);
            const name2 = this.readField(item, 'Delivery address - Name 2', false, false);
            const houseNumber = this.readField(item, 'Delivery address - House number', false, false);
            const street = this.readField(item, 'Delivery address - Street', false, false);
            const postalCode = this.readField(item, 'Delivery address - Postal code', false, false);
            const city = this.readField(item, 'Delivery address - City', false, false);
            const state = this.readField(item, 'Delivery address - State/Province/Area', false, false);
            const country = this.readField(item, 'Delivery address - Country', false, false);
            const vendorMaterialNo = this.readField(item, 'Vendor material no', false, false);
            const internalComment = this.readField(item, 'Internal comment (line item)', false, false);
            const externalComment = this.readField(item, 'External comment (line item)', false, false);

            const excelDeliveryAddressFields = {name, name2, houseNumber, street, postalCode, city, state, country};
            const isAnyExcelAddressFieldProvided = Object.values(excelDeliveryAddressFields).some((field) => field !== undefined && field !== null && field !== '');

            if (!description) {
                try {
                    const plantValue = plant?.value || '';
                    const materialData = await getMaterialByCodePromiseLambda($, getMaterialByCode, materialNumber, plantValue);

                    if (materialData && materialData.length > 0) {
                        const materialInfo = JSON.parse(materialData[0]);
                        if (materialInfo && materialInfo.length > 0) {
                            description = materialInfo[0].materialDescription;
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching material description for material number ${materialNumber}:`, error);
                }
            }

            let deliveryAddress;
            if (isAnyExcelAddressFieldProvided) {
                deliveryAddress = {
                    name: name,
                    name2: name2,
                    houseNumber: houseNumber,
                    street: street,
                    postalCode: postalCode,
                    city: city,
                    state: state,
                    country: country,
                    manuallyModified: false,
                };
            } else {
                deliveryAddress = prToUpdate.dataSources.userDefaults.deliveryAddress || EMPTY_DELIVERY_ADDRESS;
            }

            if (prToUpdate.hasOwnProperty('pr')) {
                if (!prToUpdate.pr.headerDetails.defaultRequestedDeliveryDate) {
                    prToUpdate.pr.headerDetails.defaultRequestedDeliveryDate = requestedDeliveryDate;
                }

                if (prToUpdate.pr.headerDetails.companyCode.value !== '' ) {
                    const isCompanyEnabled = await checkCompanyCodePromise($, checkCompanyCode, prToUpdate.pr.headerDetails.companyCode.value);

                    if (isCompanyEnabled?.[0] === false) {
                        prToUpdate.errorPrs.push({id: id, msg: `DYN functionality it's not enabled for Company Code ${prToUpdate.pr.headerDetails.companyCode.value || ''}.`});
                        continue;
                    }
                }

                prToUpdate.pr.materials.push(new Material(
                    prToUpdate.pr.materials.length,
                    materialNumber,
                    description,
                    quantity,
                    uoM,
                    EMPTY_LABEL_VALUE,
                    price,
                    currency,
                    per,
                    taxCode,
                    '',
                    '',
                    '',
                    requestedDeliveryDate,
                    plant,
                    storageLoc,
                    deliveryAddress,
                    '',
                    freeItem,
                    false,
                    vendorMaterialNo,
                    '',
                    false,
                    externalComment,
                    internalComment,
                    '',
                    '',
                    'Blank',
                    '',
                    undefined,
                    [],
                    '',
                    '',
                    '0',
                    convertSourceToLabelValue('NCT'),
                ));

                prToUpdate.pr.headerDetails.defaultPlant = plant;
            }
        }

        // Read only the materials with a PSL material Id (and an EPR id)
        const PSLItems = itemSheet
            .filter((item) => (this.readField(item, 'EPR Id', false, false) !== '') &&
                (this.readField(item, 'PSL Material ID', false, false) !== ''));

        const PSLIds = PSLItems
            .map((item) => this.readField(item, 'PSL Material ID', false, false));

        if (PSLIds.length > 0) {
            const chunkArray = (array, size) => {
                const chunks = [];
                for (let i = 0; i < array.length; i += size) {
                    chunks.push(array.slice(i, i + size));
                }
                return chunks;
            };

            let pslResponse = [];
            for (const idChunk of chunkArray(PSLIds, 100)) {
                const chunkInfo = await getPSLInformationLambda($, getPSLInformation, idChunk);
                const chunkData = JSON.parse(chunkInfo[0]).data || [];
                pslResponse = pslResponse.concat(chunkData);
            }
            if ( !pslResponse) {
                prsToImport.forEach((prItem)=>{
                    const invalidArr = PSLItems.map((pslItem) => {
                        return {id: this.readField(pslItem, 'EPR Id', false, false), materialPSLId: this.readField(pslItem, 'PSL Material ID', false, false)};
                    });
                    prItem.invalidPslInformation = [...invalidArr];
                });
            } else {
                const responseMap = new Map();
                pslResponse.forEach((item) => {
                    responseMap.set(item.pslMaterialId, item);
                });

                for (const item of PSLItems) {
                    const id = this.readField(item, 'EPR Id', false, false);
                    const materialPSLId = this.readField(item, 'PSL Material ID', false, false);
                    const prToUpdate = prsToImport.find((prToImport) => prToImport.id === id);

                    if (!responseMap.get(materialPSLId)) {
                        prToUpdate.invalidPslInformation = [{id: id, materialPSLId: materialPSLId}];
                        continue;
                    }

                    const pslCurrency = this.readField(responseMap.get(materialPSLId), 'currencyCode', true, false);

                    const pslRequestedDeliveryDate = this.readField(responseMap.get(materialPSLId), 'supplierLeadTime', false, false);
                    const calculatedRequestedDeliveryDate = addNBusinessDays(new Date(), pslRequestedDeliveryDate || 5).toUTCString();

                    const pslPlants = responseMap.get(materialPSLId).plants || [];
                    const pslPlantValue = pslPlants.length > 0 ? pslPlants[0] : null;
                    const pslPlant = pslPlantValue ? createLabelValue(pslPlantValue, pslPlantValue) : null;

                    const dataSources = {
                        psl: {
                            currency: pslCurrency,
                            requestedDeliveryDate: calculatedRequestedDeliveryDate,
                            plant: pslPlant,
                            vendorMaterialNumber: this.readField(responseMap.get(materialPSLId), 'supplierPartId', false, false),
                            quantity: this.readField(item, 'Quantity', false, false),
                            unitOfMeasure: this.readField(responseMap.get(materialPSLId), 'unitOfMeasure', false, false),
                            priceUnit: this.readField(responseMap.get(materialPSLId), 'unitPrice', false, false),
                            pricePer: this.readField(responseMap.get(materialPSLId), 'pricePer', false, false),
                            description: this.readField(responseMap.get(materialPSLId), 'shortDescription', false, false),
                        },
                        excel: {
                            plant: this.readField(item, 'Plant (line item)', true, false),
                            storageLocation: this.readField(item, 'Storage Location (line item)', true, false),
                            vendorMaterialNumber: this.readField(item, 'Vendor material no', false, false),
                            quantity: this.readField(item, 'Quantity', false, false),
                            unitOfMeasure: this.readField(item, 'Unit of measure', false, false),
                            priceUnit: this.readField(item, 'Price', false, false),
                            pricePer: this.readField(item, 'Price per', false, false),
                            currency: this.readField(item, 'Currency', true, false),
                            freeItem: this.readField(item, 'Free Item?', false, true),
                            taxCode: this.readField(item, 'Tax code', true, false),
                            requestedDeliveryDate: this.readField(item, 'Requested delivery date', false, false),
                            internalComment: this.readField(item, 'Internal comment (line item)', false, false),
                            externalComment: this.readField(item, 'External comment (line item)', false, false),
                            description: this.readField(item, 'Description', false, false),
                        },
                        userDefaults: {
                            currency: prToUpdate.dataSources.userDefaults.currency,
                            plant: prToUpdate.dataSources.userDefaults.plant,
                            storageLocation: prToUpdate.dataSources.userDefaults.storageLocation,
                            taxCode: prToUpdate.dataSources.userDefaults.taxCode,
                        },
                        default: {
                            requestedDeliveryDate: prToUpdate.dataSources.default.requestedDeliveryDate,
                        },
                    };

                    const l1Category = `${this.readField(responseMap.get(materialPSLId), 'spendCat1Code', false, false)} - ${this.readField(responseMap.get(materialPSLId), 'spendCat1DisplayValue', false, false)}`;
                    const l3Category = `${this.readField(responseMap.get(materialPSLId), 'spendCat3Code', false, false)} - ${this.readField(responseMap.get(materialPSLId), 'spendCat3DisplayValue', false, false)}`;

                    const materialNumber = `${this.readField(responseMap.get(materialPSLId), 'materialNumber', false, false)} - ${this.readField(responseMap.get(materialPSLId), 'materialName', false, false)}`;
                    let description = getPropertyValue('description', dataSources, {}, 'items');
                    const quantity = getPropertyValue('quantity', dataSources, {}, 'items');
                    const uoM = getPropertyValue('unitOfMeasure', dataSources, {}, 'items');
                    const price = getPropertyValue('priceUnit', dataSources, {}, 'items');
                    const per = getPropertyValue('pricePer', dataSources, {}, 'items');
                    const taxCode = getPropertyValue('taxCode', dataSources, {}, 'items');
                    const currency = getPropertyValue('currency', dataSources, {}, 'items') || prToUpdate.dataSources.userDefaults.currency;
                    let plant = getPropertyValue('plant', dataSources, {}, 'items') || EMPTY_LABEL_VALUE;
                    const supplierStatus = this.readField(responseMap.get(materialPSLId), 'statusName', false, false);
                    const vendorMaterialNumber = getPropertyValue('vendorMaterialNumber', dataSources, {}, 'items') || '';
                    const pslId = this.readField(responseMap.get(materialPSLId), 'pslId', false, false);
                    const pslMaterialId = this.readField(responseMap.get(materialPSLId), 'pslMaterialId', false, false);
                    const supplierName = this.readField(responseMap.get(materialPSLId), 'childSupplierName', false, false);
                    const supplierCode = this.readField(responseMap.get(materialPSLId), 'childSupplierCode', false, false);
                    const requestedDeliveryDate = getPropertyValue('requestedDeliveryDate', dataSources, {}, 'items');
                    const storageLocation = getPropertyValue('storageLocation', dataSources, {}, 'items') || EMPTY_LABEL_VALUE;
                    const internalComment = getPropertyValue('internalComment', dataSources, {}, 'items');
                    const externalComment = getPropertyValue('externalComment', dataSources, {}, 'items');
                    const freeItem = this.readField(item, 'Free Item?', false, true);

                    if (!description) {
                        try {
                            const plantValue = plant?.value || '';
                            const materialData = await getMaterialByCodePromiseLambda($, getMaterialByCode, materialNumber, plantValue);

                            if (materialData && materialData.length > 0) {
                                const materialInfo = JSON.parse(materialData[0]);
                                if (materialInfo && materialInfo.length > 0) {
                                    description = materialInfo[0].materialDescription;
                                }
                            }
                        } catch (error) {
                            console.error(`Error fetching material description for material number ${materialNumber}:`, error);
                        }
                    }

                    // Populate dataSources with PSL info
                    prToUpdate.dataSources.psl.vendorName = supplierName;
                    prToUpdate.dataSources.psl.vendorCode = supplierCode;
                    const wayOfBuyingArray = responseMap.get(materialPSLId).wayOfBuying;
                    if (!prToUpdate.dataSources.psl.wayOfBuyingArray) {
                        prToUpdate.dataSources.psl.wayOfBuyingArray = wayOfBuyingArray;
                    } else {
                        // Merge arrays and remove duplicates
                        prToUpdate.dataSources.psl.wayOfBuyingArray = [...new Set([...prToUpdate.dataSources.psl.wayOfBuyingArray, ...wayOfBuyingArray])];
                    }

                    // Add material to the previously created PR
                    if (prToUpdate.hasOwnProperty('pr')) {
                        let newCompanyCode = companyCode;
                        let isCompanyCodeValid = this.codeMatches(responseMap.get(materialPSLId)?.companyCodes, companyCode);
                        if (!isCompanyCodeValid ) {
                            newCompanyCode = EMPTY_LABEL_VALUE;
                            if (companyCodeUD !== '') {
                                isCompanyCodeValid = this.codeMatches(responseMap.get(materialPSLId)?.companyCodes, companyCodeUD);
                                if (isCompanyCodeValid) {
                                    newCompanyCode = createLabelValue(companyCodeUD, companyCodeUD);
                                } else {
                                    prToUpdate.warningPRs.push({id: id, msg: 'No valid Company Code found'});
                                }
                            } else {
                                prToUpdate.warningPRs.push({id: id, msg: 'No valid Company Code found'});
                            }
                        }
                        prToUpdate.dataSources.psl.companyCode = newCompanyCode;

                        const plantFromExcel = this.readField(item, 'Plant (line item)', true, false);

                        if (this.codeMatches(responseMap.get(materialPSLId)?.plants, plantFromExcel)) {
                            plant = checkLabelValueObject(plantFromExcel);
                        } else if (plantUD !== '') {
                            if (this.codeMatches(responseMap.get(materialPSLId)?.plants, plantUD)) {
                                plant = checkLabelValueObject(plantUD);
                            } else {
                                prToUpdate.warningPRs.push({id: id, msg: 'No valid Plant found'});
                            }
                        } else {
                            prToUpdate.warningPRs.push({id: id, msg: 'No valid Plant found'});
                        }


                        prToUpdate.pr.materials.push(new Material(
                            prToUpdate.pr.materials.length,
                            materialNumber,
                            description,
                            quantity,
                            uoM,
                            '',
                            price,
                            currency,
                            per,
                            taxCode,
                            '',
                            l1Category,
                            l3Category,
                            requestedDeliveryDate,
                            plant,
                            storageLocation,
                            EMPTY_DELIVERY_ADDRESS,
                            '',
                            freeItem,
                            false,
                            vendorMaterialNumber,
                            '',
                            false,
                            externalComment,
                            internalComment,
                            '',
                            '',
                            supplierStatus,
                            pslId,
                            undefined,
                            [],
                            pslMaterialId,
                            '',
                            '0',
                            convertSourceToLabelValue('PSL'),
                        ));

                        prToUpdate.pr.source = SOURCES.PSL;
                        prToUpdate.pr.vendorCode = supplierCode;
                        prToUpdate.pr.vendorName = supplierName;
                        prToUpdate.pr.headerDetails.defaultPlant = plant;
                        prToUpdate.pr.editableSupplier = Array.from(String(supplierCode)).every((char) => char === '9');
                        prToUpdate.pr.headerDetails.companyCode = newCompanyCode;
                        prToUpdate.pr.headerDetails.supplierStatus = supplierStatus;
                    }
                }
            }
        }

        const validItems = itemSheet
            .filter((item) => (this.readField(item, 'EPR Id', false, false) !== ''));

        // And then we add the financial info
        for (const item of validItems) {
            const id = this.readField(item, 'EPR Id', false, false);
            const prToUpdate = prsToImport.find((prToImport) => prToImport.id === id);
            if (!prToUpdate || (prToUpdate && ((prToUpdate.invalidPslInformation.length > 0 || prToUpdate.errorPrs.length > 0) || !prToUpdate.pr))) {
                continue;
            }

            const wbsRaw = this.readField(item, 'WBS', false, false) || '';
            const costCenterRaw = this.readField(item, 'Cost center', false, false) || '';
            const profitCenterRaw= this.readField(item, 'Profit center', false, false)|| '';
            const glNumberRaw = this.readField(item, 'GL number', false, false) || '';
            const allocatedRaw = this.readField(item, 'Allocated for', false, false)|| '';
            const allocTypeRaw = this.readField(item, 'Allocation Type', false, false)?.toLowerCase() || 'percentage';

            let accountType = EMPTY_LABEL_VALUE;
            if (wbsRaw && costCenterRaw && !profitCenterRaw) {
                accountType = createLabelValue('Statistical WBS', 'S');
            } else if (wbsRaw) {
                accountType = createLabelValue('WBS / Project', 'P');
            } else if (costCenterRaw) {
                accountType = createLabelValue('Cost Center', 'K');
            } else if (profitCenterRaw) {
                accountType = createLabelValue('Profit Center', '2');
            }

            const wbsParts = wbsRaw.split(';').map((s) => s.trim()).filter(Boolean);
            const costCenterParts = costCenterRaw.split(';').map((s) => s.trim()).filter(Boolean);
            const profitCenterParts= profitCenterRaw.split(';').map((s) => s.trim()).filter(Boolean);
            const glNumberParts = glNumberRaw.split(';').map((s) => s.trim()).filter(Boolean);
            const allocatedParts = allocatedRaw.split(';').map((s) => s.trim()).filter(Boolean);

            const multipleSplits = (
                wbsParts.length > 1 || costCenterParts.length > 1 ||
                profitCenterParts.length > 1 || glNumberParts.length > 1 ||
                allocatedParts.length > 1
            );

            if (!multipleSplits) {
                prToUpdate.pr.financialInfo.areAccountSplitByMaterial = false;
                prToUpdate.pr.financialInfo.accounts.push(new Account(
                    EMPTY_LABEL_VALUE,
                    createLabelValue(glNumberRaw, glNumberRaw),
                    accountType,
                    createLabelValue(wbsRaw, wbsRaw),
                    createLabelValue(costCenterRaw, costCenterRaw),
                    createLabelValue(profitCenterRaw, profitCenterRaw),
                    Number(allocatedRaw || 0),
                    allocTypeRaw,
                    '',
                    '',
                    false,
                ));
            } else {
                prToUpdate.pr.financialInfo.areAccountSplitByMaterial = true;

                const maxParts = Math.max(
                    wbsParts.length, costCenterParts.length,
                    profitCenterParts.length, glNumberParts.length,
                    allocatedParts.length,
                );

                const newAccounts = [];
                for (let i = 0; i < maxParts; i++) {
                    const wbsValue = wbsParts[i] || wbsParts[0] || '';
                    const costCenterValue = costCenterParts[i] || costCenterParts[0] || '';
                    const profitCenterVal = profitCenterParts[i]|| profitCenterParts[0]|| '';
                    const glNumValue = glNumberParts[i] || glNumberParts[0] || '';
                    const allocValue = allocatedParts[i] || allocatedParts[0] || '0';

                    newAccounts.push(new Account(
                        EMPTY_LABEL_VALUE,
                        createLabelValue(glNumValue, glNumValue),
                        accountType,
                        createLabelValue(wbsValue, wbsValue),
                        createLabelValue(costCenterValue, costCenterValue),
                        createLabelValue(profitCenterVal, profitCenterVal),
                        Number(allocValue),
                        allocTypeRaw,
                        '',
                        '',
                        false,
                    ));
                }

                const indexForThisMaterial = prToUpdate.pr.financialInfo.accountDetailsByMaterial.length;
                const materialForThisRow = prToUpdate.pr.materials[indexForThisMaterial];

                prToUpdate.pr.financialInfo.accountDetailsByMaterial.push(
                    new AccountDetailsByMaterial(materialForThisRow, newAccounts),
                );
            }
        }
        // After everything is set up, modify the PR according to some rules
        for (const prToImport of prsToImport) {
            if (!prToImport.hasOwnProperty('pr') || (prToImport && ((prToImport.invalidPslInformation.length > 0 || prToImport.errorPrs.length > 0) || !prToImport.pr))) {
                continue;
            }
            // Assign an EPR-id
            prToImport.pr.EPRId = await getUniquePrId($);

            // Assign values from datasSources, based on priority
            const dataSources = prToImport.dataSources;

            // Supplier
            prToImport.pr.vendorCode = getPropertyValue('vendorCode', dataSources, {validator: vendorCodeValidator}, 'header');
            if (!prToImport.pr.vendorName || prToImport.pr.vendorName === 'Undefined') {
                const vendorName = getPropertyValue('vendorName', dataSources, {}, 'header');
                prToImport.pr.vendorName = vendorName || prToImport.pr.vendorName;
            }

            // Way of Buying
            const wayOfBuyingLabelValue = determineWayOfBuying(dataSources);
            const wob = formatWaysOfBuyingByLabel(wayOfBuyingLabelValue ? wayOfBuyingLabelValue.label : null);
            prToImport.pr.wayOfBuying = new WaysOfBuying(wob.currentValue, wob.availableOptions);

            // Spender and Requester
            const spenderValue = getPropertyValue('spender', dataSources, {validator: labelValueValidator}, 'header');
            const requesterValue = getPropertyValue('requester', dataSources, {validator: labelValueValidator}, 'header');
            prToImport.pr.spender = spenderValue;
            prToImport.pr.requester = requesterValue;

            // Company code
            prToImport.pr.headerDetails.companyCode = getPropertyValue('companyCode', dataSources, {validator: labelValueValidator}, 'header');

            // Purchase Organization
            prToImport.pr.headerDetails.purchaseOrganization = getPropertyValue('purchaseOrganization', dataSources, {validator: labelValueValidator}, 'header');

            // Purchasing Group
            prToImport.pr.headerDetails.purchasingGroup = getPropertyValue('purchasingGroup', dataSources, {validator: labelValueValidator}, 'header');

            // PO Sending Options
            prToImport.pr.sendingOptions.requester = getPropertyValue('sendPOToRequester', dataSources, {}, 'header');
            prToImport.pr.sendingOptions.spender = getPropertyValue('sendPOToSpender', dataSources, {}, 'header');
            prToImport.pr.sendingOptions.other = getPropertyValue('sendPOToOther', dataSources, {}, 'header');
            prToImport.pr.sendingOptions.otherAddress = getPropertyValue('othersAddress', dataSources, {}, 'header');
            prToImport.pr.sendingOptions.supplier = getPropertyValue('sendPOToSupplier', dataSources, {}, 'header');
            prToImport.pr.sendingOptions.supplierAddresses = [getPropertyValue('supplierAddress', dataSources, {}, 'header')];
            prToImport.pr.sendingOptions.hidePrice = getPropertyValue('hidePrice', dataSources, {}, 'header');

            // grBasedInvoice and ers
            prToImport.pr.headerDetails.grBasedInvoiceVerification = getPropertyValue('grBasedInvoice', dataSources, {}, 'header');
            prToImport.pr.headerDetails.ers = getPropertyValue('ers', dataSources, {}, 'header');

            // ordering address and invoice partner
            prToImport.pr.headerDetails.orderingAddress = getPropertyValue('orderingAddress', dataSources, {}, 'header');
            prToImport.pr.headerDetails.invoicePartner = getPropertyValue('invoicePartner', dataSources, {}, 'header');

            // incoterms
            prToImport.pr.headerDetails.incoterms = getPropertyValue('incoterms', dataSources, {
                defaultValue: {
                    data: {
                        label: '',
                        value: '',
                    },
                    manuallyModified: false,
                },
            }, 'header');

            // If the WoB is "Framework", change quantity, account and requested delivery date
            if (prToImport.pr.wayOfBuying.currentValue.value === 'FO') {
                prToImport.pr.materials.forEach((m) => {
                    m.quantity = 1;
                    m.requestedDeliveryDate = prToImport.pr.headerDetails.validityEnd;
                });
                prToImport.pr.financialInfo.areAccountSplitByMaterial ?
                    prToImport.pr.financialInfo.accountDetailsByMaterial.forEach((adbm) => adbm.accounts.forEach((a) => a.itemCategory = {label: 'B - Limit', value: 'B'})) :
                    prToImport.pr.financialInfo.accounts.forEach((a) => a.itemCategory = {label: 'B - Limit', value: 'B'});
            }
            // If there are different plants in the line items, use a blank value for the default plant (it will be replaced by the one from user defaults)
            if (!prToImport.pr.materials.every((m, i, arr) => m.plant.value === arr[0].plant.value)) {
                prToImport.pr.headerDetails.defaultPlant = EMPTY_LABEL_VALUE;
            }

            if (!prToImport.pr.headerDetails.defaultRequestedDeliveryDate) {
                prToImport.pr.headerDetails.defaultRequestedDeliveryDate = addNBusinessDays(new Date(), 5).toUTCString();
            }
        }
        return prsToImport;
    }

    /**
     * Normalize a code from any source (raw string, number, or {label, value} object)
     * into a comparable string. Different sources represent codes differently.
     * @param {*} x
     * @return {string}
     */
    normalizeCode(x) {
        if (x === null || x === undefined) {
            return '';
        }
        if (typeof x === 'object') {
            return String(x.value ?? x.label ?? '');
        }
        return String(x);
    }

    /**
     * True if `candidate` matches any entry in `list`, regardless of whether either
     * side is a string, number, or {label, value} object. Empty never matches.
     * @param {Array<*>} list
     * @param {*} candidate
     * @return {boolean}
     */
    codeMatches(list, candidate) {
        const target = this.normalizeCode(candidate);
        if (target === '') {
            return false;
        }
        return (list || []).some((item) => this.normalizeCode(item) === target);
    }

    /**
     * Check if the field is set or not, and return the value
     * @param {object} data
     * @param {string} fieldName
     * @param {boolean} isLabelValue
     * @param {boolean} isBoolean
     * @param {string} defaultValue
     * @param {boolean} toUpperCase
     * @return {string|LabelValue|boolean}
     */
    readField(data, fieldName, isLabelValue, isBoolean, defaultValue = '', toUpperCase = false) {
        // If data is undefined, return an empty labelValue object, or an empty string
        if (!data) {
            return isLabelValue ? checkLabelValueObject(data) : defaultValue;
        }

        let value;
        // We need to check if the value is a string before trimming it, to avoid trying to trim numbers
        if (typeof data[fieldName] === 'string' || data[fieldName] instanceof String) {
            value = data[fieldName]?.trim();
        } else {
            value = data[fieldName];
        }

        if (isLabelValue) {
            let newValue = value?.toString();
            if (newValue && toUpperCase) {
                newValue = newValue.toUpperCase();
            }
            return checkLabelValueObject(newValue);
        } else if (isBoolean) {
            if (value === 'Yes') {
                return true;
            }
            if (value === 'No') {
                return false;
            }
            return undefined;
        } else {
            let newValue = value?.toString();
            if (newValue && toUpperCase) {
                newValue = newValue.toUpperCase();
            }
            return newValue || defaultValue;
        }
    }

    /**
     * fetches the companyCode, purchaseOrg, plant, storage location
     * @param {!BrickContext} $
     * @param {*} getMasterDataMultipleObjects
     * @param {PrToImport[]} prsToImport
     * @return {Object}
     */
    fetchMasterData($, getMasterDataMultipleObjects, prsToImport) {
        const safeSplit = (rawValue, padLen=0) => {
            const value = rawValue || '';
            return value
                .split(';')
                .map((part) => {
                    const trimmed = part.trim();
                    return (padLen > 0) ?
                        trimmed.padStart(padLen, '0') :
                        trimmed;
                })
                .filter(Boolean);
        };

        const getValueOrEmpty = (labelValueObj) => {
            return labelValueObj?.value || '';
        };

        /**
         * @param {Object[]} obj
         * @return {string[]}
         */
        const findObjectToFetch = (obj) => {
            if (!obj) {
                return [];
            }
            return obj
                .flat()
                .filter((o) => !!o.value && o.value === o.label) // Remove empty values and keep only the ones with the same label and value, that means that we are doing a call to fetch the information
                .filter((value, index, self) => // Remove duplicates
                    index === self.findIndex((o) => o.label === value.label))
                .map((o) => o.value); // keep only the value
        };

        const findAllPlants = () => {
            const headerVal = prsToImport.map((prToImport) => prToImport.pr.headerDetails.defaultPlant);
            const materialsVal = [];
            prsToImport.forEach((prToImport) => prToImport.pr.materials.forEach((m) => materialsVal.push(m.plant)));
            return headerVal.concat(materialsVal);
        };
        const financialInfoToFetch = prsToImport.map((prToImport) => {
            let accounts;
            if (prToImport.pr.financialInfo.areAccountSplitByMaterial) {
                accounts = prToImport.pr.financialInfo.accountDetailsByMaterial.map((a) => a.accounts).flat();
            } else {
                accounts = prToImport.pr.financialInfo.accounts;
            }
            accounts = accounts || [];
            return {
                costCenters: accounts.map((account) => ({costCenter: account.costCenter, companyCode: prToImport.pr.headerDetails.companyCode})),
                wbss: accounts.map((account) => ({wbs: account.wbs, companyCode: prToImport.pr.headerDetails.companyCode})),
                profitCenters: accounts.map((account) => ({profitCenter: account.profitCenter, companyCode: prToImport.pr.headerDetails.companyCode})),
                glAccounts: accounts.map((account) => ({glAccount: account.glNumber, companyCode: prToImport.pr.headerDetails.companyCode})),
            };
        }).reduce((acc, current) => {
            acc.costCenters = acc.costCenters.concat(current.costCenters);
            acc.wbss = acc.wbss.concat(current.wbss);
            acc.profitCenters = acc.profitCenters.concat(current.profitCenters);
            acc.glAccounts = acc.glAccounts.concat(current.glAccounts);
            return acc;
        }, {costCenters: [], wbss: [], profitCenters: [], glAccounts: []});

        const objectsToFetch = {
            companyCodes: findObjectToFetch(prsToImport.map((prToImport) => prToImport.pr.headerDetails.companyCode)),
            purchaseOrgs: findObjectToFetch(prsToImport.map((prToImport) => prToImport.pr.headerDetails.purchaseOrganization)),
            purchasingGroups: findObjectToFetch(prsToImport.map((prToImport) => prToImport.pr.headerDetails.purchasingGroup)),
            plants: findObjectToFetch(findAllPlants()),
            storageLocations: prsToImport.map((prToImport) => ({storageLocation: prToImport.pr.headerDetails.defaultStorageLocation, plant: prToImport.pr.headerDetails.defaultPlant}))
                .filter((o) => !!o.storageLocation.value && o.storageLocation.value === o.storageLocation.label) // Remove empty values and keep only the ones with the same label and value, that means that we are doing a call to fetch the information
                .filter((value, index, self) => // Remove duplicates
                    index === self.findIndex((o) => o.storageLocation.label === value.storageLocation.label))
                .map((o) => ({plant: o.plant.value, storageLocation: o.storageLocation.value})), // keep only the value
            wbss: financialInfoToFetch.wbss
                .filter((o) => o.wbs && o.wbs.value && o.wbs.value === o.wbs.label)
                .filter((value, index, self) => index === self.findIndex((x) => x.wbs.label === value.wbs.label))
                .flatMap((o) => {
                    const splitted = safeSplit(getValueOrEmpty(o.wbs), 0);
                    return splitted.map((wbsPart) => ({
                        companyCode: getValueOrEmpty(o.companyCode),
                        wbs: wbsPart,
                    }));
                }),
            costCenters: financialInfoToFetch.costCenters
                .filter((o) => o.costCenter && o.costCenter.value && o.costCenter.value === o.costCenter.label)
                .filter((value, index, self) => index === self.findIndex((x) => x.costCenter.label === value.costCenter.label))
                .flatMap((o) => {
                    const original = getValueOrEmpty(o.costCenter);
                    const splitted = safeSplit(original, 10);
                    return splitted.map((ccPart) => ({
                        companyCode: getValueOrEmpty(o.companyCode),
                        costCenter: ccPart,
                    }));
                }),
            profitCenters: financialInfoToFetch.profitCenters
                .filter((o) => o.profitCenter && o.profitCenter.value && o.profitCenter.value === o.profitCenter.label)
                .filter((value, index, self) => index === self.findIndex((x) => x.profitCenter.label === value.profitCenter.label))
                .flatMap((o) => {
                    const splitted = safeSplit(getValueOrEmpty(o.profitCenter), 10);
                    return splitted.map((profitPart) => ({
                        companyCode: getValueOrEmpty(o.companyCode),
                        profitCenter: profitPart,
                    }));
                }),
            glNumbers: financialInfoToFetch.glAccounts
                .filter((o) => o.glAccount && o.glAccount.value && o.glAccount.value === o.glAccount.label)
                .filter((value, index, self) => index === self.findIndex((x) => x.glAccount.label === value.glAccount.label))
                .flatMap((o) => {
                    const splitted = safeSplit(getValueOrEmpty(o.glAccount), 10);
                    return splitted.map((glPart) => ({
                        companyCode: getValueOrEmpty(o.companyCode),
                        glAccount: glPart,
                    }));
                }),
            paymentTerms: prsToImport.map((prToImport) => ({companyCode: prToImport.pr.headerDetails.companyCode, purchaseOrg: prToImport.pr.headerDetails.purchaseOrganization}))
                .filter((value, index, self) => // Remove duplicates
                    index === self.findIndex((o) => o.companyCode.label === value.companyCode.label))
                .map((o) => ({companyCode: o.companyCode.value, purchaseOrg: o.purchaseOrg.value})), // keep only the value
            materialNumbers: [...new Set(prsToImport
                .map((prToImport) => prToImport.pr.materials)
                .flat()
                .map((material) => material.name.split(' - ')[0].padStart(18, '0')))],
            suppliers: [...new Set(prsToImport
                .map((prToImport) => prToImport.pr.vendorCode.padStart(10, '0')))],
            taxCodes: prsToImport.map((prToImport) => ({companyCode: prToImport.pr.headerDetails.companyCode.value, taxCode: prToImport.pr.materials.map((m) => m.taxCode)}))
                .map((o) => ({companyCode: o.companyCode, taxCode: o.taxCode.filter((taxCode) => !!taxCode.value && taxCode.value === taxCode.label)})) // Remove empty values and keep only the ones with the same label and value, that means that we are doing a call to fetch the information
                .filter((o) => o.taxCode.length !== 0)
                .map((o) => ({companyCode: o.companyCode, taxCode: o.taxCode.filter((value, index, self) => // Remove duplicates
                    index === self.findIndex((o) => o.label === value.label))}))
                .map((o) => ({taxCode: o.taxCode.map((taxCode) => taxCode.value), companyCode: o.companyCode})), // keep only the value
        };

        const companyCodesPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Company Code', objectsToFetch.companyCodes);
        const purchaseOrgsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Purchase Organization', objectsToFetch.purchaseOrgs);
        const purchasingGroupsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Purchasing Group', objectsToFetch.purchasingGroups);
        const plantsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Plant', objectsToFetch.plants);
        const storageLocationsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Storage Location', objectsToFetch.storageLocations);
        const wbsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'WBS', objectsToFetch.wbss);
        const costCenterPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Cost Center', objectsToFetch.costCenters);
        const profitCenterPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Profit Center', objectsToFetch.profitCenters);
        const glAccountPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'GL Account', objectsToFetch.glNumbers);
        const paymentTermsPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Payment Terms', objectsToFetch.paymentTerms);
        const l3CategoriesPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'L3 Category', objectsToFetch.materialNumbers);
        const materialTypesPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Material Type', objectsToFetch.materialNumbers);
        const suppliersPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Supplier', objectsToFetch.suppliers);
        const taxCodesPromise = getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Tax Code', objectsToFetch.taxCodes);

        return Promise.all([
            companyCodesPromise,
            purchaseOrgsPromise,
            purchasingGroupsPromise,
            plantsPromise,
            storageLocationsPromise,
            wbsPromise,
            costCenterPromise,
            profitCenterPromise,
            glAccountPromise,
            paymentTermsPromise,
            l3CategoriesPromise,
            materialTypesPromise,
            suppliersPromise,
            taxCodesPromise,
        ]);
    }

    /**
     * Fetch the plants' addresses
     * @param {BrickContext} $
     * @param {[]} results
     * @param {any} getMasterDataMultipleObjects
     * @return {Promise<string>}
     */
    fetchDeliveryAddress($, results, getMasterDataMultipleObjects) {
        const addressNumbers = JSON.parse(results[3][0]).map((r) => r.adrnr);
        return getMasterDataMultipleObjectsLambda($, getMasterDataMultipleObjects, 'Address from Plant', addressNumbers);
    }

    /**
     * Read the results from the APIs and return a formatted object
     * @param {[]} results
     * @return {{purchaseOrgs: *, purchasingGroups: *, plants: *, glAccounts: *, companyCodes: *, storageLocations: *, wbss: *, profitCenters: *, costCenters: *}}
     */
    readResults(results) {
        return {
            companyCodes: JSON.parse(results[0][0]).map((r) => createLabelValue(`${r.bukrs} - ${r.butxt}`, r.bukrs)),
            purchaseOrgs: JSON.parse(results[1][0]).map((r) => createLabelValue(`${r.ekorg} - ${r.ekotx}`, r.ekorg)),
            purchasingGroups: JSON.parse(results[2][0]).map((r) => createLabelValue(`${r.ekgrp} - ${r.eknam}`, r.ekgrp)),
            plants: JSON.parse(results[3][0]).map((r) => ({...createLabelValue(`${r.werks} - ${r.name1}`, r.werks), addressNumber: r.adrnr})),
            storageLocations: results[4][0].map((apiResponse) => JSON.parse(apiResponse)[0]).filter((r) => r).map((r) => createLabelValue(`${r.StorgLoc} - ${r.Name1}`, r.StorgLoc)),
            wbss: results[5][0].map((apiResponse) => JSON.parse(apiResponse)[0]).filter((r) => r).map((r) => createLabelValue(`${r.WBSElemnt} - ${r.PostShort1}`, r.WBSElemnt)),
            costCenters: JSON.parse(results[6][0]).map((r) => createLabelValue(`${r.kostl} - ${r.name1}`, r.kostl)),
            profitCenters: results[7][0].map((apiResponse) => JSON.parse(apiResponse)[0]).filter((r) => r).map((r) => createLabelValue(`${r.ProfitCentr} - ${r.ShortTxt}`, r.ProfitCentr)),
            glAccounts: results[8][0].map((apiResponse) => JSON.parse(apiResponse)[0]).filter((r) => r).map((r) => createLabelValue(`${r.GLAccNo} - ${r.GLAccLngtxt}`, r.GLAccNo)),
            paymentTerms: results[9][0].map((apiResponse) => JSON.parse(apiResponse)[0]).filter((r) => r).map((r) => createLabelValue(`${r.Term} - ${r.NameVAT}`, r.Term)),
            l3Categories: JSON.parse(results[10][0]).map((r) => createLabelValue(`${r.matkl} - ${r.wgbez}`, r.matnr)),
            materialTypes: JSON.parse(results[11][0]).map((r) => createLabelValue(r.mtart, r.matnr)),
            suppliers: JSON.parse(results[12][0]).map((r) => createLabelValue(r.name1, r.lifnr)),
            taxCodes: results[13][0].map((apiResponse) => JSON.parse(apiResponse)).filter((r) => r).flat().map((r) => createLabelValue(`${r.PurchsCode} - ${r.NameVAT}`, r.PurchsCode)),
            // Addresses from plants must be the last one, as it is added in a second time
            addressesFromPlants: JSON.parse(results[14][0]).map((r) => ({
                name: r.name1,
                name2: r.name2,
                houseNumber: r.house_num1,
                street: r.street,
                postalCode: r.post_code1,
                city: r.city1,
                state: r.county,
                country: r.nation,
                manuallyModified: false,
                addressNumber: r.addrnumber,
            })),
        };
    };

    /**
     * Update a PR with the given Master Data
     * @param {PurchaseRequest} pr
     * @param {{}} md
     */
    updatePR(pr, md) {
        if (!md) {
            return;
        }

        // Update PR details
        if (pr.vendorName === 'Undefined') {
            if (md.suppliers.some((sup) => sup.value === pr.vendorCode.padStart(10, '0'))) {
                pr.vendorName = md.suppliers.find((sup) => sup.value === pr.vendorCode.padStart(10, '0'))?.label;
            }
        }
        // Update header details
        if (md.companyCodes.some((cc) => cc.value === pr.headerDetails.companyCode.value)) {
            pr.headerDetails.companyCode = md.companyCodes.find((cc) => cc.value === pr.headerDetails.companyCode.value);
        }
        if (md.purchaseOrgs.some((porg) => porg.value === pr.headerDetails.purchaseOrganization.value)) {
            pr.headerDetails.purchaseOrganization = md.purchaseOrgs.find((porg) => porg.value === pr.headerDetails.purchaseOrganization.value);
        }
        if (md.purchasingGroups.some((pgrp) => pgrp.value === pr.headerDetails.purchasingGroup.value)) {
            pr.headerDetails.purchasingGroup = md.purchasingGroups.find((pgrp) => pgrp.value === pr.headerDetails.purchasingGroup.value);
        }
        if (md.plants.some((plant) => plant.value === pr.headerDetails.defaultPlant.value)) {
            pr.headerDetails.defaultPlant = md.plants.find((plant) => plant.value === pr.headerDetails.defaultPlant.value);
            pr.headerDetails.defaultDeliveryAddress = md.addressesFromPlants.find((add) => add.addressNumber === pr.headerDetails.defaultPlant.addressNumber) || EMPTY_DELIVERY_ADDRESS;
        }
        if (md.storageLocations.some((sl) => sl.value === pr.headerDetails.defaultStorageLocation.value)) {
            pr.headerDetails.defaultStorageLocation = md.storageLocations.find((sl) => sl.value === pr.headerDetails.defaultStorageLocation.value);
        }
        if (md.paymentTerms.some((pt) => pt.value === pr.headerDetails.defaultStorageLocation.value)) {
            pr.headerDetails.defaultStorageLocation = md.storageLocations.find((sl) => sl.value === pr.headerDetails.defaultStorageLocation.value);
        }
        // Update materials
        if (haveCommonElements(md.plants.map((plant) => plant.value), pr.materials.map((m) => m.plant.value))) {
            pr.materials.forEach((m) => {
                m.plant = md.plants.find((plant) => plant.value === m.plant.value) || EMPTY_LABEL_VALUE;
                m.deliveryAddress = md.addressesFromPlants.find((add) => add.addressNumber === m.plant.addressNumber) || EMPTY_DELIVERY_ADDRESS;
            });
        }
        if (haveCommonElements(md.l3Categories.map((l3) => l3.value), pr.materials.map((m) => m.name.split(' - ')[0].padStart(18, '0')))) {
            pr.materials.forEach((m) => {
                m.l3Category = md.l3Categories.find((l3) => l3.value === m.name.split(' - ')[0].padStart(18, '0'))?.label || '';
                m.type = md.materialTypes.find((mt) => mt.value === m.name.split(' - ')[0].padStart(18, '0'))?.label || '';
            });
        }
        if (haveCommonElements(md.taxCodes.map((tc) => tc.value), pr.materials.map((m) => m.taxCode.value))) {
            pr.materials.forEach((m) => {
                m.taxCode = md.taxCodes.find((tc) => tc.value === m.taxCode.value) || EMPTY_LABEL_VALUE;
            });
        }
        // Update financial info
        if (pr.financialInfo.areAccountSplitByMaterial) {
            pr.financialInfo.accountDetailsByMaterial.forEach((adbm) => {
                adbm.accounts.forEach((a) => {
                    // Update WBS
                    if (a.wbs?.value) {
                        const foundWbs = md.wbss.find((wbs) => wbs.value === a.wbs.value);
                        a.wbs = foundWbs || EMPTY_LABEL_VALUE;
                    } else {
                        a.wbs = EMPTY_LABEL_VALUE;
                    }
                    // Update cost center
                    if (a.costCenter?.value) {
                        const ccValue = a.costCenter.value.padStart(10, '0');
                        const foundCostCenter = md.costCenters.find((cc) => cc.value === ccValue);
                        a.costCenter = foundCostCenter || EMPTY_LABEL_VALUE;
                    } else {
                        a.costCenter = EMPTY_LABEL_VALUE;
                    }
                    // Update profit center
                    if (a.profitCenter?.value) {
                        const pcValue = a.profitCenter.value.padStart(10, '0');
                        const foundProfitCenter = md.profitCenters.find((pc) => pc.value === pcValue);
                        a.profitCenter = foundProfitCenter || EMPTY_LABEL_VALUE;
                    } else {
                        a.profitCenter = EMPTY_LABEL_VALUE;
                    }
                    // Update GL account
                    if (a.glNumber?.value) {
                        const glValue = a.glNumber.value.padStart(10, '0');
                        const foundGlAccount = md.glAccounts.find((gl) => gl.value === glValue);
                        a.glNumber = foundGlAccount || EMPTY_LABEL_VALUE;
                    } else {
                        a.glNumber = EMPTY_LABEL_VALUE;
                    }
                });
            });
        } else {
            pr.financialInfo.accounts.forEach((a) => {
                // Update WBS
                if (a.wbs?.value) {
                    a.wbs = md.wbss.find((wbs) => wbs.value === a.wbs.value) || EMPTY_LABEL_VALUE;
                } else {
                    a.wbs = EMPTY_LABEL_VALUE;
                }
                // Update cost center
                if (a.costCenter?.value) {
                    const ccValue = a.costCenter.value.padStart(10, '0');
                    const found = md.costCenters.find((cc) => cc.value === ccValue);
                    a.costCenter = found || EMPTY_LABEL_VALUE;
                } else {
                    a.costCenter = EMPTY_LABEL_VALUE;
                }
                // Update profit center
                if (a.profitCenter?.value) {
                    const pcValue = a.profitCenter.value.padStart(10, '0');
                    a.profitCenter = md.profitCenters.find((pc) => pc.value === pcValue) || EMPTY_LABEL_VALUE;
                } else {
                    a.profitCenter = EMPTY_LABEL_VALUE;
                }
                // Update GL account
                if (a.glNumber?.value) {
                    const glValue = a.glNumber.value.padStart(10, '0');
                    a.glNumber = md.glAccounts.find((gl) => gl.value === glValue) || EMPTY_LABEL_VALUE;
                } else {
                    a.glNumber = EMPTY_LABEL_VALUE;
                }
            });
        }
    };
}

registerBrick('019091745d4faba5d288', ConvertExcelPayloadToPRJson);
